import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { newsletterUnsubscribeByToken, newsletterUnsubscribe, formatApiError } from "@/lib/api";
import { CheckCircle, WarningCircle, SpinnerGap } from "@phosphor-icons/react";

function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

export default function NewsletterUnsubscribePage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  // Token-based one-click flow
  const [tokenStatus, setTokenStatus] = useState(token ? "loading" : "no-token");
  const [tokenErr, setTokenErr] = useState("");

  // Email form fallback (when no token in URL)
  const [email, setEmail] = useState("");
  const [formBusy, setFormBusy] = useState(false);
  const [formDone, setFormDone] = useState(false);
  const [formErr, setFormErr] = useState("");

  useEffect(() => {
    if (!token) return;
    newsletterUnsubscribeByToken(token)
      .then(() => setTokenStatus("success"))
      .catch((ex) => {
        setTokenErr(formatApiError(ex));
        setTokenStatus("error");
      });
  }, [token]);

  const onFormSubmit = async (e) => {
    e.preventDefault();
    if (!isValidEmail(email)) {
      setFormErr("Please enter a valid email address.");
      return;
    }
    setFormErr("");
    setFormBusy(true);
    try {
      await newsletterUnsubscribe(email);
      setFormDone(true);
    } catch (ex) {
      setFormErr(formatApiError(ex));
    } finally {
      setFormBusy(false);
    }
  };

  // ---- Token one-click flow ----

  if (tokenStatus === "loading") {
    return (
      <div className="max-w-md mx-auto px-4 md:px-8 py-16 flex flex-col items-center gap-4">
        <SpinnerGap size={32} className="text-blue-600 animate-spin" />
        <p className="text-sm text-slate-400">Processing your request…</p>
      </div>
    );
  }

  if (tokenStatus === "success") {
    return (
      <div className="max-w-md mx-auto px-4 md:px-8 py-16">
        <div className="flex items-center justify-center w-12 h-12 bg-green-50 border border-green-200 mb-6 rounded-xl">
          <CheckCircle size={24} className="text-green-600" />
        </div>
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
          / Unsubscribed
        </span>
        <h1 className="font-bold text-4xl mt-3 mb-3 text-slate-900">
          You're unsubscribed.
        </h1>
        <p className="text-sm text-slate-500 mb-8 leading-relaxed">
          You've been removed from the TrendMing daily newsletter. You won't receive any more digest emails.
        </p>
        <Link
          to="/"
          className="text-xs text-slate-400 hover:text-slate-700 uppercase tracking-wider"
        >
          ← Back to TrendMing
        </Link>
      </div>
    );
  }

  if (tokenStatus === "error") {
    return (
      <div className="max-w-md mx-auto px-4 md:px-8 py-16">
        <div className="flex items-center justify-center w-12 h-12 bg-red-50 border border-red-200 mb-6 rounded-xl">
          <WarningCircle size={24} className="text-red-500" />
        </div>
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
          / Link invalid
        </span>
        <h1 className="font-bold text-4xl mt-3 mb-3 text-slate-900">
          Link expired.
        </h1>
        <p className="text-sm text-slate-500 mb-6">
          {tokenErr || "This unsubscribe link is invalid. Use the form below instead."}
        </p>
        <UnsubscribeForm
          email={email} setEmail={setEmail}
          formBusy={formBusy} formDone={formDone} formErr={formErr}
          onFormSubmit={onFormSubmit}
        />
      </div>
    );
  }

  // ---- No token — show email form directly ----
  return (
    <div className="max-w-md mx-auto px-4 md:px-8 py-16">
      <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
        / Unsubscribe
      </span>
      <h1 className="font-bold text-4xl mt-3 mb-3 text-slate-900">
        Unsubscribe from TrendMing.
      </h1>
      <p className="text-sm text-slate-500 mb-8 leading-relaxed">
        Enter your email address and we'll remove you from the newsletter immediately.
      </p>
      <UnsubscribeForm
        email={email} setEmail={setEmail}
        formBusy={formBusy} formDone={formDone} formErr={formErr}
        onFormSubmit={onFormSubmit}
      />
    </div>
  );
}

function UnsubscribeForm({ email, setEmail, formBusy, formDone, formErr, onFormSubmit }) {
  if (formDone) {
    return (
      <div className="flex items-start gap-3 p-4 bg-green-50 border border-green-200 rounded-xl">
        <CheckCircle size={20} className="text-green-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-bold text-slate-900">You've been unsubscribed.</p>
          <p className="text-xs text-slate-500 mt-1">
            If you didn't request this, you can resubscribe at any time.
          </p>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={onFormSubmit} className="space-y-3">
      <input
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="your@email.com"
        className="w-full bg-slate-50 border border-slate-200 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30 px-3 py-3 text-sm rounded-lg text-slate-900"
        aria-label="Email address to unsubscribe"
      />
      {formErr && <p className="text-xs text-red-500">{formErr}</p>}
      <button
        type="submit"
        disabled={formBusy}
        className="w-full bg-slate-700 hover:bg-slate-800 disabled:opacity-50 text-white font-bold py-3 text-xs rounded-lg uppercase tracking-wider transition-colors"
      >
        {formBusy ? "Processing…" : "Unsubscribe"}
      </button>
    </form>
  );
}
